import { useState, useEffect } from 'react';
import { photoGalleryData } from '../../../mocks/photoGallery';

export default function PhotoGallery() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const categories = ['All', 'NutriCare', 'The Hispanic League', 'Gables Voice'];

  const filteredPhotos = selectedCategory === 'All' 
    ? photoGalleryData 
    : photoGalleryData.filter(photo => photo.category === selectedCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('gallery');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section id="gallery" className="relative py-24 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-3">
            <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase text-[#800000] mb-4 px-6 py-2 border border-[#800000]/20 bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>Gallery</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-black text-center mb-4 tracking-tight" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
            Photo Gallery
          </h2>
          <p className="text-base text-center text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Moments captured from community service, leadership, and personal achievements
          </p>
        </div>

        {/* Category Filter */}
        <div className={`mb-12 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-[#800000] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Photo Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
          {filteredPhotos.map((photo, index) => (
            <div
              key={index}
              className={`bg-white group cursor-pointer transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 50}ms` }}
              onClick={() => setSelectedImage(index)}
            >
              <div className="relative overflow-hidden h-80 bg-gray-100">
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-full object-cover object-center transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300"></div>
                
                {/* Overlay Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                  <h3 className="text-lg font-bold text-white mb-2 tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {photo.title}
                  </h3>
                  <p className="text-sm text-white/90 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {photo.description}
                  </p>
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {photo.category}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox Modal */}
        {selectedImage !== null && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            </button>
            
            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img
                src={filteredPhotos[selectedImage].url}
                alt={filteredPhotos[selectedImage].title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              <div className="mt-6 text-center">
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {filteredPhotos[selectedImage].title}
                </h3>
                <p className="text-base text-white/80" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {filteredPhotos[selectedImage].description}
                </p>
              </div>
            </div>

            {/* Navigation Arrows */}
            {selectedImage > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(selectedImage - 1);
                }}
                className="absolute left-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-2xl w-6 h-6 flex items-center justify-center"></i>
              </button>
            )}
            {selectedImage < filteredPhotos.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(selectedImage + 1);
                }}
                className="absolute right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line text-2xl w-6 h-6 flex items-center justify-center"></i>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
